import { createSignal } from '@verclam/pulsar';

/**
 * What the document sidebar shows: primitives and string arrays only, never a View or a viewer object.
 */
export type DocumentPanelTarget =
	| {
			readonly type: 'field';
			readonly fieldKey: string;
			readonly occurrenceKey: string | null;
	  }
	| {
			readonly type: 'condition';
			/** Keys of the fields the clicked text depends on, the innermost region's first, without duplicates. */
			readonly fieldKeys: string[];
	  }
	| {
			readonly type: 'list-item';
			readonly listKey: string;
			readonly index: number;
	  }
	| { readonly type: 'empty-list'; readonly listKey: string }
	| {
			readonly type: 'computed';
			readonly fieldId: string;
			readonly label: string;
			readonly dependsOnIds: string[];
	  }
	| { readonly type: 'gone'; readonly reason: 'field' | 'item' | 'condition' };

/** The open target, `null` when the sidebar is closed. */
const documentPanelSignal = createSignal<DocumentPanelTarget | null>(null);

let opener: HTMLElement | null = null;

/** Every opening assigns a new object, so re-opening the same target still notifies. */
export const openDocumentPanel = (target: DocumentPanelTarget): void => {
	if (documentPanelSignal.peek() === null && typeof document !== 'undefined') {
		opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
	}

	documentPanelSignal.value = { ...target };
};

export const closeDocumentPanel = (): void => {
	documentPanelSignal.value = null;
};

/** The element that had the focus when the sidebar opened; returned once, then forgotten. */
export const takeDocumentPanelOpener = (): HTMLElement | null => {
	const taken = opener;
	opener = null;
	return taken;
};

export default documentPanelSignal;
